import React, { useEffect, useMemo } from 'react';
import { Timer, Info, Clock } from 'lucide-react';
import { useBreakoutRooms } from '../../../context/BreakoutRoomContext';

function ToggleSwitch({ checked, onChange }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-9 h-5 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#6a1fbf]" />
    </label>
  );
}

export default function SettingsTab() {
  const { session, updateSettings, rooms, setRoomTimer } = useBreakoutRooms();
  const settings = session?.settings || {
    timerEnabled: true,
    timerDuration: 900,
    individualTimers: false,
    autoCloseOnTimerEnd: true,
    endNotificationEnabled: true,
    endNotificationMinutes: 1,
  };

  const handleChange = (key, value) => {
    updateSettings({ ...settings, [key]: value });
  };

  const durationMinutes = Math.round(settings.timerDuration / 60);

  const allNotificationOptions = [
    { value: 0.5, label: '30 seconds' },
    { value: 1, label: '1 minute' },
    { value: 2, label: '2 minutes' },
    { value: 3, label: '3 minutes' },
    { value: 5, label: '5 minutes' },
  ];

  // Only show options strictly less than the session duration
  const validNotificationOptions = useMemo(() => {
    if (!settings.timerEnabled) return allNotificationOptions;
    return allNotificationOptions.filter(opt => opt.value < durationMinutes);
  }, [settings.timerEnabled, durationMinutes]);

  // Auto-correct notification value when it becomes invalid (e.g. duration lowered)
  useEffect(() => {
    if (!settings.endNotificationEnabled || validNotificationOptions.length === 0) return;
    const isValid = validNotificationOptions.some(o => o.value === settings.endNotificationMinutes);
    if (!isValid) {
      const best = validNotificationOptions[validNotificationOptions.length - 1].value;
      handleChange('endNotificationMinutes', best);
    }
  }, [validNotificationOptions, settings.endNotificationMinutes, settings.endNotificationEnabled]);

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      {/* Session Timer Card */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#6a1fbf]/20 flex items-center justify-center">
              <Timer className="w-4 h-4 text-[#6a1fbf]" />
            </div>
            <div>
              <h3 className="text-white text-base font-semibold">Session Timer</h3>
              <p className="text-gray-400 text-xs">Set a time limit for breakout sessions</p>
            </div>
          </div>
          <ToggleSwitch
            checked={settings.timerEnabled}
            onChange={(v) => handleChange('timerEnabled', v)}
          />
        </div>

        {settings.timerEnabled && (
          <div className="space-y-4 pl-11">
            {/* Duration Slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-sm">Default Duration: {durationMinutes} minutes</span>
              </div>
              <input
                type="range"
                min={1}
                max={60}
                value={durationMinutes}
                onChange={(e) => handleChange('timerDuration', parseInt(e.target.value) * 60)}
                className="w-full h-1.5 bg-gray-600 rounded-full appearance-none cursor-pointer accent-[#6a1fbf]"
                style={{
                  background: `linear-gradient(to right, #6a1fbf 0%, #6a1fbf ${(durationMinutes / 60) * 100}%, #4b5563 ${(durationMinutes / 60) * 100}%, #4b5563 100%)`,
                }}
              />
            </div>

            {/* Individual Room Timers */}
            <div className="flex items-center justify-between">
              <div>
                <span className="text-white text-sm">Individual room timers</span>
                <p className="text-gray-400 text-xs">Set different timers per room</p>
              </div>
              <ToggleSwitch
                checked={settings.individualTimers}
                onChange={(v) => handleChange('individualTimers', v)}
              />
            </div>

            {/* Per-room timer config (shown when individual timers enabled and at
                least one room is still open). Closed rooms are hidden — the
                backend refuses to start a timer on a closed room anyway, so
                showing them here is misleading. */}
            {settings.individualTimers && rooms.some(r => r.isActive !== false) && (
              <div className="space-y-2 pl-2 border-l-2 border-[#6a1fbf]/30">
                <p className="text-gray-400 text-xs mb-1">Room durations:</p>
                {rooms.filter(r => r.isActive !== false).map((room) => {
                  const roomMin = room.timerDuration
                    ? Math.round(room.timerDuration / 60)
                    : durationMinutes;
                  return (
                    <div key={room.roomId} className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Clock className="w-3 h-3 text-gray-500 flex-shrink-0" />
                        <span className="text-white text-xs truncate">{room.roomName}</span>
                      </div>
                      <select
                        value={roomMin}
                        onChange={(e) => setRoomTimer(room.roomId, parseInt(e.target.value))}
                        className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-[#6a1fbf] w-24 flex-shrink-0"
                      >
                        {[5, 10, 15, 20, 30, 45, 60].map(v => (
                          <option key={v} value={v} className="bg-[#001233]">{v} min</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Auto-close */}
            <div className="flex items-center justify-between">
              <span className="text-white text-sm">Auto-close when timer ends</span>
              <ToggleSwitch
                checked={settings.autoCloseOnTimerEnd}
                onChange={(v) => handleChange('autoCloseOnTimerEnd', v)}
              />
            </div>
          </div>
        )}
      </div>

      {/* End Notification Card */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#6a1fbf]/20 flex items-center justify-center">
              <Info className="w-4 h-4 text-[#6a1fbf]" />
            </div>
            <div>
              <h3 className="text-white text-base font-semibold">End Notification</h3>
              <p className="text-gray-400 text-xs">Notify participants before session ends</p>
            </div>
          </div>
          <ToggleSwitch
            checked={settings.endNotificationEnabled}
            onChange={(v) => handleChange('endNotificationEnabled', v)}
          />
        </div>

        {settings.endNotificationEnabled && (
          <div className="pl-11 space-y-3">
            <p className="text-gray-400 text-sm">
              Notify {settings.endNotificationMinutes < 1
                ? `${Math.round(settings.endNotificationMinutes * 60)} seconds`
                : `${settings.endNotificationMinutes} minute${settings.endNotificationMinutes > 1 ? 's' : ''}`} before end
            </p>
            <select
              value={settings.endNotificationMinutes}
              onChange={(e) => handleChange('endNotificationMinutes', parseFloat(e.target.value))}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#6a1fbf] w-full"
            >
              {validNotificationOptions.length === 0 ? (
                <option value={0.5} className="bg-[#001233]">Duration too short for notification</option>
              ) : (
                validNotificationOptions.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-[#001233]">{opt.label}</option>
                ))
              )}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
